import PageHeader from '../components/ui/PageHeader';
import { useConfirmation } from '../contexts/ConfirmationContext';
import React, { useEffect, useState } from 'react';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { formatDateTimeGerman } from '../utils/dateFormatter';
import { useAuth } from '../contexts/AuthContext';
import { reviewService, Review } from '../services/reviewService';
import { CalendarIcon, UserIcon, BookOpenIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';

export const ReviewsPage: React.FC = () => {
  const { hasRole } = useAuth();
  const confirm = useConfirmation();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'recent'>('recent');
  const [reload, setReload] = useState(0);

  useEffect(() => {
    const fetchReviews = async () => {
      if (!hasRole(['administrator', 'redakteur'])) {
        setError('Keine Berechtigung');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true); setError(null);
        const allReviews = await reviewService.getAllReviews();
        
        // Sortiere nach Erstellungsdatum (neueste zuerst)
        const sortedReviews = allReviews.sort((a, b) => 
          new Date(b.$createdAt).getTime() - new Date(a.$createdAt).getTime()
        );

        if (filter === 'recent') {
          // Zeige nur die letzten 20 Rezensionen
          setReviews(sortedReviews.slice(0, 20));
        } else {
          setReviews(sortedReviews);
        }
      } catch (error) {
        console.error('Fehler beim Laden der Rezensionen:', error);
        setError('Fehler beim Laden der Rezensionen');
      } finally {
        setIsLoading(false);
      }
    };

    fetchReviews();
  }, [hasRole, filter, reload]);

  const handleDeleteReview = async (reviewId: string) => {
    if (!(await confirm({ title: 'Rezension löschen?', description: 'Die Rezension wird dauerhaft entfernt. Diese Aktion kann nicht rückgängig gemacht werden.', confirmLabel: 'Rezension löschen', danger: true }))) {
      return;
    }

    try {
      await reviewService.deleteReview(reviewId);
      setReviews(reviews.filter(review => review.$id !== reviewId));
      toast.success('Rezension erfolgreich gelöscht');
    } catch (error) {
      console.error('Fehler beim Löschen der Rezension:', error);
      toast.error('Fehler beim Löschen der Rezension');
    }
  };


  const renderStars = (rating: number | null) => {
    if (!rating) return null;
    
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <StarIconSolid
            key={star}
            className={`w-4 h-4 ${
              star <= rating ? 'text-yellow-400' : 'text-gray-300'
            }`}
          />
        ))}
        <span className="ml-1 text-sm text-gray-600">({rating}/5)</span>
      </div>
    );
  };

  if (!hasRole(['administrator', 'redakteur'])) {
    return (
      <div className="text-center py-8">
        <div className="text-red-500 text-lg font-medium mb-2">Keine Berechtigung</div>
        <div className="text-gray-600">Du hast keine Berechtigung, diese Seite zu besuchen.</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="text-red-500 text-lg font-medium mb-2">Fehler beim Laden</div>
        <div role="alert" className="text-gray-600">{error}</div><button className="btn-secondary mt-4" onClick={() => setReload(n => n + 1)}>Erneut versuchen</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <BookOpenIcon className="w-8 h-8 text-blue-600" />
          </div>
          <div className="ml-4">
            <PageHeader title={<>Rezensionen</>} description={<>{reviews.length} Rezension{reviews.length !== 1 ? 'en' : ''} {filter === 'recent' ? 'aus den letzten Einträgen' : 'gesamt'}</>} />
          </div>
        </div>

        {/* Filter */}
        <div className="flex space-x-2">
          <button
            onClick={() => setFilter('recent')}
            className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              filter === 'recent'
                ? 'bg-blue-100 text-blue-800 border border-blue-300'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
            }`}
          >
            Neueste
          </button>
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              filter === 'all'
                ? 'bg-blue-100 text-blue-800 border border-blue-300'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
            }`}
          >
            Alle
          </button>
        </div>
      </div>

      {/* Reviews List */}
      {reviews.length === 0 ? (
        <div className="text-center py-8">
          <BookOpenIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Keine Rezensionen gefunden</h3>
          <p className="text-gray-600">Es wurden noch keine Rezensionen eingereicht.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <div key={review.$id} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* Header */}
                  <div className="flex items-center space-x-4 mb-3">
                    <div className="flex items-center space-x-2">
                      <UserIcon className="w-4 h-4 text-gray-500" />
                      <span className="font-medium text-gray-900">{review.autor}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CalendarIcon className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-600">{formatDateTimeGerman(review.$createdAt)}</span>
                    </div>
                    {renderStars(review.bewertung)}
                  </div>

                  {/* Book Info */}
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {review.buchtitel}
                    </h3>
                    <p className="text-sm text-gray-600">von {review.buchautor}</p>
                  </div>

                  {/* Review Text */}
                  <div className="mb-4">
                    <p className="text-gray-700 leading-relaxed">
                      {review.rezensionstext}
                    </p>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span>{review.wortanzahl} Wörter</span>
                    <span>{review.rezensionstext.length} Zeichen</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      review.status === 'published' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {review.status === 'published' ? 'Veröffentlicht' : review.status}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="ml-4 flex flex-col space-y-2">
                  <button
                    onClick={() => handleDeleteReview(review.$id)}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
                  >
                    Löschen
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
